import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useChurch } from '../context/ChurchContext';
import { requiredMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

// ─── Icons ────────────────────────────────────────────────────────────────────
const Ico = (d) => () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>{d}</svg>
);
const UserIcon   = Ico(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>);
const GroupIcon  = Ico(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>);
const ShieldIcon = Ico(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>);
const PlusIcon   = Ico(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>);
const EditIcon   = Ico(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>);
const TrashIcon  = Ico(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></>);
const CloseIcon  = Ico(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>);
const CheckIcon  = Ico(<polyline points="20 6 9 17 4 12"/>);
const GridIcon   = Ico(<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></>);
const SaveIcon   = Ico(<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></>);

// ─── Styles ───────────────────────────────────────────────────────────────────
const card = { background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'12px' };
const inp  = { width:'100%', boxSizing:'border-box', padding:'8px 11px', borderRadius:'8px', border:'1px solid var(--border)', background:'var(--card-bg)', color:'var(--text-primary)', fontSize:'0.84rem', outline:'none', fontFamily:'inherit' };
const lbl  = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:'4px', letterSpacing:'0.04em' };
const btnP = { display:'inline-flex', alignItems:'center', gap:'6px', padding:'9px 16px', borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'0.84rem', fontWeight:600, background:'var(--primary)', color:'#fff', fontFamily:'inherit' };
const btnS = { display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', cursor:'pointer', fontSize:'0.84rem', fontWeight:500, background:'var(--card-bg)', color:'var(--text-primary)', border:'1px solid var(--border)', fontFamily:'inherit' };

const ROLE_COLORS = {
  superadmin_platform: '#7c3aed',
  admin:               '#2563eb',
  superviseur:         '#4f46e5',
  responsable:         '#0891b2',
  accueil:             '#059669',
  consultation:        '#6b7280',
};
const ROLE_LABELS = {
  superadmin_platform: 'Super Admin',
  admin:               'Admin',
  superviseur:         'Superviseur',
  responsable:         'Responsable',
  accueil:             'Accueil',
  consultation:        'Consultation',
};

// ─── Badge rôle ───────────────────────────────────────────────────────────────
function RoleBadge({ role, displayRole }) {
  const color = ROLE_COLORS[role] || '#6b7280';
  return (
    <span style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'0.69rem', fontWeight:700, color, background:`${color}18`, whiteSpace:'nowrap' }}>
      {displayRole || ROLE_LABELS[role] || role}
    </span>
  );
}

// ─── Modal droits d'un utilisateur — plein écran ─────────────────────────────
const PERMISSION_MODE_OPTIONS = [
  { value: 'combine',    label: 'Cumuler rôle + groupe',   help: "L'utilisateur a accès si SON RÔLE OU SES GROUPES le permettent (le plus large des deux)." },
  { value: 'group_only', label: 'Groupe uniquement',       help: "Seuls les groupes assignés comptent — le rôle affiché devient un simple libellé, sans effet sur les droits." },
  { value: 'role_only',  label: 'Rôle uniquement',         help: "Seul le rôle compte — les groupes assignés à cet utilisateur n'ont aucun effet sur ses droits." },
];

function UserGroupModal({ user, groups, allPerms, onClose, onSave }) {
  const [selectedGroups, setSelectedGroups] = useState((user.groups || []).map(g => g.id));
  const [selectedPerms,  setSelectedPerms]  = useState((user.user_permissions || []).map(p => p.id));
  const [permissionMode, setPermissionMode] = useState(user.permission_mode || 'combine');
  const [tab,    setTab]    = useState('groups');
  const [saving, setSaving] = useState(false);

  const toggleGroup = (id) => setSelectedGroups(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.patch(`/auth/users/${user.id}/set_groups/`,      { groups:      selectedGroups }),
        api.patch(`/auth/users/${user.id}/set_permissions/`, { permissions: selectedPerms  }),
        api.patch(`/auth/users/${user.id}/`, { permission_mode: permissionMode }),
      ]);
      toast.success('Droits mis à jour');
      onSave();
    } catch { toast.error('Erreur sauvegarde'); }
    finally  { setSaving(false); }
  };

  const initials = ((user.first_name?.[0]||'') + (user.last_name?.[0]||'') || user.username?.[0] || '?').toUpperCase();

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'stretch', justifyContent:'center', padding:'24px' }}>
      <div style={{ ...card, width:'100%', maxWidth:'1100px', maxHeight:'100%', display:'flex', flexDirection:'column', boxShadow:'0 40px 100px rgba(0,0,0,0.4)', borderRadius:16, padding:0 }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:42, height:42, borderRadius:'50%', background:'var(--primary)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', fontWeight:700 }}>
              {initials}
            </div>
            <div>
              <h3 style={{ margin:0, fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)' }}>
                {user.first_name} {user.last_name || user.username}
              </h3>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:2 }}>
                <RoleBadge role={user.role} displayRole={user.display_role} />
                <span style={{ fontSize:'0.76rem', color:'var(--text-secondary)' }}>{user.username}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer', color:'var(--text-secondary)', display:'flex', padding:8 }}><CloseIcon /></button>
        </div>

        {/* Mode de combinaison rôle / groupe */}
        <div style={{ padding:'16px 28px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <label style={{ ...lbl, marginBottom:8 }}>Comment combiner le rôle et les groupes ?</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {PERMISSION_MODE_OPTIONS.map(opt => {
              const active = permissionMode === opt.value;
              return (
                <button key={opt.value} type="button" onClick={() => setPermissionMode(opt.value)} title={opt.help}
                  style={{ padding:'8px 14px', borderRadius:8, border:`2px solid ${active ? 'var(--primary)' : 'var(--border)'}`, background: active ? 'rgba(99,102,241,0.08)' : 'var(--page-bg)', color: active ? 'var(--primary)' : 'var(--text-primary)', fontWeight: active ? 700 : 500, fontSize:'0.82rem', cursor:'pointer', fontFamily:'inherit' }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p style={{ margin:'8px 0 0', fontSize:'0.75rem', color:'var(--text-secondary)' }}>
            {PERMISSION_MODE_OPTIONS.find(o => o.value === permissionMode)?.help}
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:0, padding:'0 28px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          {[['groups','Groupes assignés'],['perms','Permissions individuelles']].map(([id,label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding:'12px 18px', border:'none', borderBottom: tab===id ? '2px solid var(--primary)' : '2px solid transparent', cursor:'pointer', fontSize:'0.85rem', fontWeight:tab===id?700:400, background:'none', color: tab===id ? 'var(--primary)' : 'var(--text-secondary)', fontFamily:'inherit', marginBottom:'-1px' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, padding:'20px 28px', overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>
          {tab === 'groups' ? (
            groups.length === 0 ? (
              <div style={{ textAlign:'center', padding:'40px 0', color:'var(--text-secondary)' }}>
                <p>Aucun groupe créé. Allez dans l'onglet <strong>Groupes</strong> pour en créer.</p>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:10, overflowY:'auto' }}>
                {groups.map(g => {
                  const checked = selectedGroups.includes(g.id);
                  return (
                    <label key={g.id} onClick={() => toggleGroup(g.id)} style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 16px', borderRadius:10, background: checked ? 'rgba(99,102,241,0.07)' : 'var(--page-bg)', border:`2px solid ${checked ? 'rgba(99,102,241,0.4)' : 'var(--border)'}`, cursor:'pointer', transition:'all 0.15s' }}>
                      <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${checked ? 'var(--primary)' : 'var(--border)'}`, background: checked ? 'var(--primary)' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                        {checked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:'0.88rem', color:'var(--text-primary)' }}>{g.name}</div>
                        <div style={{ fontSize:'0.73rem', color:'var(--text-secondary)', marginTop:2 }}>
                          {g.permissions?.length || 0} permission{(g.permissions?.length||0)!==1?'s':''}
                        </div>
                      </div>
                      {checked && <span style={{ fontSize:'0.68rem', color:'var(--primary)', fontWeight:700 }}>Assigné</span>}
                    </label>
                  );
                })}
              </div>
            )
          ) : (
            <DualPermSelector allPerms={allPerms} selected={selectedPerms} onChange={setSelectedPerms} />
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 28px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <button onClick={onClose} style={btnS}>Annuler</button>
          <button onClick={save} disabled={saving} style={{ ...btnP, opacity:saving?0.7:1 }}><SaveIcon />{saving?'Enregistrement…':'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Sélecteur 2 colonnes style Django admin ─────────────────────────────────
function DualPermSelector({ allPerms, selected, onChange }) {
  const allList  = Object.values(allPerms || {}).flat();
  const [leftSel,  setLeftSel]  = useState([]);  // ids sélectionnés dans colonne gauche
  const [rightSel, setRightSel] = useState([]);  // ids sélectionnés dans colonne droite
  const [searchL,  setSearchL]  = useState('');
  const [searchR,  setSearchR]  = useState('');

  const available = allList.filter(p => !selected.includes(p.id));
  const chosen    = allList.filter(p =>  selected.includes(p.id));

  const filtL = available.filter(p => !searchL || p.name.toLowerCase().includes(searchL.toLowerCase()) || p.codename.toLowerCase().includes(searchL.toLowerCase()));
  const filtR = chosen.filter(p   => !searchR || p.name.toLowerCase().includes(searchR.toLowerCase()) || p.codename.toLowerCase().includes(searchR.toLowerCase()));

  const toggleL = (id) => setLeftSel(s  => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const toggleR = (id) => setRightSel(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const addSelected    = () => { onChange([...selected, ...leftSel]);               setLeftSel([]); };
  const addAll         = () => { onChange(allList.map(p=>p.id));                    setLeftSel([]); };
  const removeSelected = () => { onChange(selected.filter(id => !rightSel.includes(id))); setRightSel([]); };
  const removeAll      = () => { onChange([]);                                      setRightSel([]); };

  const colStyle = { flex:1, display:'flex', flexDirection:'column', minWidth:0, minHeight:0 };
  const listStyle = { flex:1, overflowY:'auto', border:'1px solid var(--border)', borderRadius:8, background:'var(--page-bg)' };
  const itemStyle = (active) => ({
    padding:'8px 12px', cursor:'pointer', fontSize:'0.82rem', userSelect:'none',
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--text-primary)',
    borderBottom:'1px solid var(--border)',
    display:'flex', justifyContent:'space-between', alignItems:'center', gap:8,
    transition:'background-color 0.12s',
  });
  const arrowBtn = (onClick, children, disabled) => (
    <button onClick={onClick} disabled={disabled} style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:8, background:'var(--card-bg)', cursor: disabled?'not-allowed':'pointer', opacity: disabled?0.4:1, fontFamily:'inherit', fontSize:'0.8rem', fontWeight:700, color:'var(--text-primary)', width:44 }}>
      {children}
    </button>
  );

  // Grouper les items disponibles par module pour affichage
  const byModule = Object.entries(allPerms || {}).map(([mod, perms]) => ({
    mod,
    items: perms.filter(p => !selected.includes(p.id) && (!searchL || p.name.toLowerCase().includes(searchL.toLowerCase()) || p.codename.toLowerCase().includes(searchL.toLowerCase()))),
  })).filter(g => g.items.length > 0);

  return (
    <div style={{ display:'flex', gap:12, height:'100%' }}>
      {/* ── Colonne gauche : Disponibles ── */}
      <div style={colStyle}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <span style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)' }}>
            Disponibles ({available.length})
          </span>
          <button onClick={addAll} disabled={available.length===0} style={{ fontSize:'0.72rem', color:'var(--primary)', background:'none', border:'none', cursor:'pointer', fontWeight:600, padding:0 }}>
            Tout ajouter →
          </button>
        </div>
        <input value={searchL} onChange={e=>setSearchL(e.target.value)} placeholder="Filtrer…" style={{ ...inp, marginBottom:6, fontSize:'0.8rem', padding:'6px 10px' }} />
        <div style={listStyle}>
          {byModule.length === 0 && <div style={{ padding:16, textAlign:'center', color:'var(--text-secondary)', fontSize:'0.82rem' }}>Aucune permission disponible</div>}
          {byModule.map(({ mod, items }) => (
            <div key={mod}>
              <div style={{ position:'sticky', top:0, zIndex:1, padding:'6px 12px', fontSize:'0.67rem', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--primary)', background:'rgba(99,102,241,0.12)', borderBottom:'1px solid var(--border)' }}>
                {mod}
              </div>
              {items.map(p => (
                <div key={p.id} onClick={() => toggleL(p.id)} style={itemStyle(leftSel.includes(p.id))}
                  onDoubleClick={() => { onChange([...selected, p.id]); }}>
                  <span>{p.name}</span>
                  <span style={{ fontSize:'0.68rem', opacity:0.6, fontFamily:'monospace', flexShrink:0 }}>{p.codename}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{ marginTop:5, fontSize:'0.7rem', color:'var(--text-secondary)' }}>Double-clic pour ajouter</div>
      </div>

      {/* ── Boutons flèches ── */}
      <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', gap:6, paddingTop:26 }}>
        {arrowBtn(addSelected,    '→', leftSel.length===0)}
        {arrowBtn(addAll,         '»', available.length===0)}
        {arrowBtn(removeSelected, '←', rightSel.length===0)}
        {arrowBtn(removeAll,      '«', chosen.length===0)}
      </div>

      {/* ── Colonne droite : Choisies ── */}
      <div style={colStyle}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <span style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)' }}>
            Sélectionnées ({chosen.length})
          </span>
          <button onClick={removeAll} disabled={chosen.length===0} style={{ fontSize:'0.72rem', color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontWeight:600, padding:0 }}>
            ← Tout retirer
          </button>
        </div>
        <input value={searchR} onChange={e=>setSearchR(e.target.value)} placeholder="Filtrer…" style={{ ...inp, marginBottom:6, fontSize:'0.8rem', padding:'6px 10px' }} />
        <div style={listStyle}>
          {chosen.length === 0 && <div style={{ padding:16, textAlign:'center', color:'var(--text-secondary)', fontSize:'0.82rem' }}>Aucune permission sélectionnée</div>}
          {chosen.filter(p => !searchR || p.name.toLowerCase().includes(searchR.toLowerCase()) || p.codename.toLowerCase().includes(searchR.toLowerCase())).map(p => (
            <div key={p.id} onClick={() => toggleR(p.id)} style={itemStyle(rightSel.includes(p.id))}
              onDoubleClick={() => onChange(selected.filter(id => id !== p.id))}>
              <span>{p.name}</span>
              <span style={{ fontSize:'0.68rem', opacity:0.6, fontFamily:'monospace', flexShrink:0 }}>{p.codename}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop:5, fontSize:'0.7rem', color:'var(--text-secondary)' }}>Double-clic pour retirer</div>
      </div>
    </div>
  );
}

// ─── Modal groupe (create/edit) — plein écran ─────────────────────────────────
function GroupModal({ group, allPerms, users, onClose, onSave }) {
  const [name,         setName]         = useState(group?.name || '');
  const [selectedPerms, setSelectedPerms] = useState((group?.permissions || []).map(p => p.id));
  const [selectedMembers, setSelectedMembers] = useState((group?.members || []).map(m => m.id));
  const [saving,       setSaving]       = useState(false);
  const { activeChurchId, isPlatformAdmin } = useChurch();

  const toggleMember = (id) => setSelectedMembers(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);

  const [nameError, setNameError] = useState('');

  const save = async () => {
    if (!name.trim()) { setNameError(requiredMessage('Nom du groupe')); return; }
    setNameError('');
    if (!group?.id && isPlatformAdmin && !activeChurchId) {
      toast.error('Sélectionnez une église avant de créer un groupe.');
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), permissions: selectedPerms };
      if (!group?.id && isPlatformAdmin) payload.church_id = activeChurchId;
      let groupId = group?.id;
      if (groupId) await api.put(`/auth/groups/${groupId}/`,  payload);
      else {
        const res = await api.post('/auth/groups/', payload);
        groupId = res.data.id;
      }
      // Synchronise les membres : ajoute le groupe à ceux cochés, le retire
      // de ceux décochés qui l'avaient auparavant.
      const previouslyIn = (group?.members || []).map(m => m.id);
      const toAdd    = selectedMembers.filter(id => !previouslyIn.includes(id));
      const toRemove = previouslyIn.filter(id => !selectedMembers.includes(id));
      await Promise.all([
        ...toAdd.map(uid => {
          const u = users.find(x => x.id === uid);
          const currentGroups = (u?.groups || []).map(g => g.id);
          return api.patch(`/auth/users/${uid}/set_groups/`, { groups: [...currentGroups, groupId] });
        }),
        ...toRemove.map(uid => {
          const u = users.find(x => x.id === uid);
          const currentGroups = (u?.groups || []).map(g => g.id).filter(gid => gid !== groupId);
          return api.patch(`/auth/users/${uid}/set_groups/`, { groups: currentGroups });
        }),
      ]);
      toast.success(group?.id ? 'Groupe modifié' : 'Groupe créé');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const totalAvail = Object.values(allPerms || {}).flat().length;

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'stretch', justifyContent:'center', padding:'24px' }}>
      <div style={{ ...card, width:'100%', maxWidth:'1100px', maxHeight:'100%', display:'flex', flexDirection:'column', boxShadow:'0 40px 100px rgba(0,0,0,0.4)', borderRadius:16, padding:0 }} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding:'18px 28px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:'rgba(99,102,241,0.12)', color:'#6366f1', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <GroupIcon />
            </div>
            <div>
              <h3 style={{ margin:0, fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)' }}>
                {group?.id ? 'Modifier le groupe' : 'Nouveau groupe de droits'}
              </h3>
              <p style={{ margin:'2px 0 0', fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                {selectedPerms.length} / {totalAvail} permissions sélectionnées
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer', color:'var(--text-secondary)', display:'flex', padding:8 }}><CloseIcon /></button>
        </div>

        {/* Nom */}
        <div style={{ padding:'16px 28px 12px', flexShrink:0, borderBottom:'1px solid var(--border)' }}>
          <label style={lbl}>Nom du groupe<Req /></label>
          <input value={name} onChange={e=>setName(e.target.value)} style={{ ...inp, maxWidth:420, ...(nameError ? { borderColor: ERROR_COLOR } : {}) }} placeholder="ex: Caissier, Secrétaire, Pasteur adjoint…" autoFocus />
          <FieldError message={nameError} />
        </div>

        {/* Membres du groupe */}
        <div style={{ padding:'14px 28px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <label style={{ ...lbl, marginBottom:8 }}>Membres ({selectedMembers.length})</label>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, maxHeight:110, overflowY:'auto' }}>
            {(users || []).filter(u => u.role !== 'superadmin_platform').map(u => {
              const checked = selectedMembers.includes(u.id);
              return (
                <button key={u.id} type="button" onClick={() => toggleMember(u.id)}
                  style={{ padding:'6px 12px', borderRadius:999, border:`1.5px solid ${checked ? 'var(--primary)' : 'var(--border)'}`, background: checked ? 'rgba(99,102,241,0.08)' : 'var(--page-bg)', color: checked ? 'var(--primary)' : 'var(--text-primary)', fontSize:'0.8rem', fontWeight: checked ? 700 : 500, cursor:'pointer' }}>
                  {checked ? '✓ ' : ''}{u.first_name || u.username}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sélecteur 2 colonnes */}
        <div style={{ flex:1, padding:'16px 28px', overflow:'hidden', display:'flex', flexDirection:'column', minHeight:0 }}>
          <label style={{ ...lbl, marginBottom:10 }}>Permissions incluses dans ce groupe</label>
          <div style={{ flex:1, minHeight:0 }}>
            <DualPermSelector allPerms={allPerms} selected={selectedPerms} onChange={setSelectedPerms} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 28px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0 }}>
          <button onClick={onClose} style={btnS}>Annuler</button>
          <button onClick={save} disabled={saving} style={{ ...btnP, opacity:saving?0.7:1 }}><SaveIcon />{saving?'Enregistrement…':'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────
export default function Permissions() {
  const [tab,      setTab]      = useState('users');
  const [users,    setUsers]    = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [allPerms, setAllPerms] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  const [editUserModal,  setEditUserModal]  = useState(null);
  const [editGroupModal, setEditGroupModal] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);

  const { activeChurchId, isPlatformAdmin } = useChurch();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const userParams = isPlatformAdmin && activeChurchId ? { church_id: activeChurchId } : {};
      const groupParams = isPlatformAdmin
        ? (activeChurchId ? { church_id: activeChurchId } : {})
        : {};
      const [uRes, gRes, pRes] = await Promise.all([
        api.get('/auth/users/?page_size=500', { params: userParams }),
        api.get('/auth/groups/?page_size=500', { params: groupParams }),
        api.get('/auth/permissions/'),
      ]);
      setUsers(Array.isArray(uRes.data)  ? uRes.data  : (uRes.data.results  || []));
      setGroups(Array.isArray(gRes.data) ? gRes.data  : (gRes.data.results  || []));
      setAllPerms(pRes.data || {});
    } catch { toast.error('Erreur chargement'); }
    finally  { setLoading(false); }
  }, [isPlatformAdmin, activeChurchId]);

  useEffect(() => { load(); }, [load]);

  const openUserModal = async (user) => {
    try {
      const res = await api.get(`/auth/users/${user.id}/permissions/`);
      setEditUserModal({ ...user, ...res.data });
    } catch { setEditUserModal(user); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Supprimer ce groupe ? Les utilisateurs membres perdront ces droits.')) return;
    try { await api.delete(`/auth/groups/${id}/`); toast.success('Groupe supprimé'); load(); }
    catch { toast.error('Erreur suppression'); }
  };

  const filteredUsers = users.filter(u =>
    !search || `${u.first_name} ${u.last_name} ${u.username} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  // Matrice : users × modules
  const allModules = Object.keys(allPerms);
  const hasAnyPerm = (user, module) => {
    const modPerms = allPerms[module] || [];
    return modPerms.some(p => (user.user_permissions || []).some(up => up.id === p.id) ||
      (user.groups || []).some(g => {
        const grp = groups.find(gr => gr.id === g.id);
        return grp?.permissions?.some(gp => gp.id === p.id);
      })
    );
  };

  return (
    <div style={{ width:'100%', boxSizing:'border-box' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:'1.45rem', fontWeight:800, color:'var(--text-primary)' }}>Gestion des droits</h1>
          <p style={{ margin:'3px 0 0', fontSize:'0.84rem', color:'var(--text-secondary)' }}>
            Groupes, permissions et accès par utilisateur
          </p>
        </div>
        {tab === 'groups' && (
          <button style={btnP} onClick={() => { setEditGroupModal(null); setShowGroupModal(true); }}>
            <PlusIcon /> Nouveau groupe
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:2, background:'#d4c9b8', border:'1px solid #bfb3a0', borderRadius:10, padding:4, width:'fit-content', marginBottom:22 }}>
        {[
          { id:'users',  label:'Utilisateurs', Icon: UserIcon  },
          { id:'groups', label:'Groupes',       Icon: GroupIcon },
          { id:'matrix', label:'Matrice',       Icon: GridIcon  },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'7px 15px', borderRadius:7, border:'none', cursor:'pointer',
            fontSize:'0.84rem', fontWeight: tab===id ? 600 : 400,
            background: tab===id ? 'var(--primary)' : 'transparent',
            color: tab===id ? '#fff' : '#3D3028', fontFamily:'inherit', transition:'all 0.15s',
          }}>
            <Icon /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'80px', color:'var(--text-secondary)' }}>Chargement…</div>
      ) : (
        <>
          {/* ══════════ UTILISATEURS ══════════ */}
          {tab === 'users' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ ...card, padding:14 }}>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher un utilisateur…" style={inp} />
              </div>

              <div style={card}>
                {filteredUsers.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--text-secondary)' }}>Aucun utilisateur trouvé.</div>
                ) : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.875rem' }}>
                      <thead>
                        <tr>
                          {['Utilisateur','Rôle','Groupes','Permissions','Actions'].map((h,i) => (
                            <th key={i} style={{ padding:'11px 16px', textAlign:'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)', borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u, idx) => (
                          <tr key={u.id} style={{ borderBottom:'1px solid var(--border)', background: idx%2===0 ? 'transparent' : 'var(--page-bg)' }}>
                            <td style={{ padding:'12px 16px' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <div style={{ width:34, height:34, borderRadius:'50%', background:'var(--primary)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.78rem', fontWeight:700, flexShrink:0 }}>
                                  {((u.first_name?.[0]||'') + (u.last_name?.[0]||'') || u.username?.[0] || '?').toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{u.first_name} {u.last_name}</div>
                                  <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)' }}>{u.username}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding:'12px 16px' }}><RoleBadge role={u.role} displayRole={u.display_role} /></td>
                            <td style={{ padding:'12px 16px' }}>
                              {u.groups?.length > 0 ? (
                                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                  {u.groups.map(g => (
                                    <span key={g.id} style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'0.69rem', fontWeight:600, background:'rgba(99,102,241,0.1)', color:'#6366f1' }}>{g.name}</span>
                                  ))}
                                </div>
                              ) : <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>—</span>}
                            </td>
                            <td style={{ padding:'12px 16px' }}>
                              <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>
                                {u.role === 'superadmin_platform' ? (
                                  <span style={{ color:'#7c3aed', fontWeight:600 }}>Accès total</span>
                                ) : (
                                  `${u.groups?.length || 0} groupe${(u.groups?.length||0)!==1?'s':''}`
                                )}
                              </span>
                            </td>
                            <td style={{ padding:'12px 16px' }}>
                              {u.role !== 'superadmin_platform' && (
                                <button onClick={() => openUserModal(u)} style={{ ...btnS, padding:'6px 12px', fontSize:'0.78rem' }}>
                                  <EditIcon /> Gérer
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════ GROUPES ══════════ */}
          {tab === 'groups' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {groups.length === 0 ? (
                <div style={{ ...card, textAlign:'center', padding:'60px 20px' }}>
                  <div style={{ color:'var(--text-secondary)', marginBottom:16, fontSize:'0.9rem' }}>
                    Aucun groupe. Créez un groupe pour regrouper des permissions et les assigner facilement.
                  </div>
                  <button style={btnP} onClick={() => { setEditGroupModal(null); setShowGroupModal(true); }}>
                    <PlusIcon /> Créer un groupe
                  </button>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px,1fr))', gap:14 }}>
                  {groups.map(g => (
                    <div key={g.id} style={{ ...card, padding:18 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:36, height:36, borderRadius:10, background:'rgba(99,102,241,0.12)', color:'#6366f1', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <GroupIcon />
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>{g.name}</div>
                            <div style={{ fontSize:'0.73rem', color:'var(--text-secondary)', marginTop:2 }}>
                              {g.member_count} membre{g.member_count!==1?'s':''} · {g.permissions?.length||0} permission{(g.permissions?.length||0)!==1?'s':''}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:2 }}>
                          <button onClick={() => { setEditGroupModal(g); setShowGroupModal(true); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', padding:5, borderRadius:6 }}><EditIcon /></button>
                          <button onClick={() => deleteGroup(g.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:5, borderRadius:6 }}><TrashIcon /></button>
                        </div>
                      </div>

                      {/* Membres */}
                      {g.members?.length > 0 && (
                        <div style={{ marginBottom:10 }}>
                          <div style={{ fontSize:'0.71rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Membres</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {g.members.slice(0,5).map(m => (
                              <span key={m.id} style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'0.7rem', background:'var(--page-bg)', border:'1px solid var(--border)', color:'var(--text-primary)' }}>
                                {m.first_name || m.username}
                              </span>
                            ))}
                            {g.members.length > 5 && <span style={{ fontSize:'0.7rem', color:'var(--text-secondary)', padding:'2px 4px' }}>+{g.members.length-5}</span>}
                          </div>
                        </div>
                      )}

                      {/* Permissions */}
                      {g.permissions?.length > 0 && (
                        <div>
                          <div style={{ fontSize:'0.71rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Permissions</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {g.permissions.slice(0,6).map(p => (
                              <span key={p.id} style={{ padding:'2px 7px', borderRadius:'99px', fontSize:'0.68rem', background:'rgba(16,185,129,0.1)', color:'#059669', fontFamily:'monospace' }}>
                                {p.codename}
                              </span>
                            ))}
                            {g.permissions.length > 6 && <span style={{ fontSize:'0.7rem', color:'var(--text-secondary)' }}>+{g.permissions.length-6}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ══════════ MATRICE ══════════ */}
          {tab === 'matrix' && (
            <div style={card}>
              {users.length === 0 || allModules.length === 0 ? (
                <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--text-secondary)' }}>
                  Pas de données à afficher.
                </div>
              ) : (
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.78rem' }}>
                    <thead>
                      <tr>
                        <th style={{ padding:'10px 16px', textAlign:'left', whiteSpace:'nowrap', fontWeight:700, color:'var(--text-secondary)', fontSize:'0.71rem', textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'2px solid var(--border)', position:'sticky', left:0, background:'var(--card-bg)', zIndex:2 }}>
                          Utilisateur
                        </th>
                        {allModules.map(m => (
                          <th key={m} style={{ padding:'10px 12px', textAlign:'center', whiteSpace:'nowrap', fontWeight:700, color:'var(--primary)', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:'2px solid var(--border)' }}>
                            {m}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, idx) => (
                        <tr key={u.id} style={{ borderBottom:'1px solid var(--border)', background: idx%2===0 ? 'transparent' : 'var(--page-bg)' }}>
                          <td style={{ padding:'10px 16px', whiteSpace:'nowrap', position:'sticky', left:0, background: idx%2===0 ? 'var(--card-bg)' : 'var(--page-bg)', zIndex:1 }}>
                            <div style={{ fontWeight:600, color:'var(--text-primary)' }}>{u.first_name} {u.last_name || u.username}</div>
                            <RoleBadge role={u.role} displayRole={u.display_role} />
                          </td>
                          {allModules.map(m => {
                            const fullAccess = u.role === 'superadmin_platform';
                            const has = fullAccess || hasAnyPerm(u, m);
                            return (
                              <td key={m} style={{ padding:'10px 12px', textAlign:'center' }}>
                                {fullAccess ? (
                                  <span title="Accès total" style={{ color:'#7c3aed' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                  </span>
                                ) : has ? (
                                  <span title="A accès" style={{ color:'#10b981' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                  </span>
                                ) : (
                                  <span style={{ color:'var(--border)', fontSize:'0.8rem' }}>—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {editUserModal && (
        <UserGroupModal
          user={editUserModal}
          groups={groups}
          allPerms={allPerms}
          onClose={() => setEditUserModal(null)}
          onSave={() => { setEditUserModal(null); load(); }}
        />
      )}
      {showGroupModal && (
        <GroupModal
          group={editGroupModal}
          allPerms={allPerms}
          users={users}
          onClose={() => { setShowGroupModal(false); setEditGroupModal(null); }}
          onSave={() => { setShowGroupModal(false); setEditGroupModal(null); load(); }}
        />
      )}
    </div>
  );
}
